import { useState, useEffect, useCallback } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Radio, RefreshCw, Shield } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { Button } from './components/ui/button';
import { SignalCard } from './components/SignalCard';
import { SubmitSignalModal } from './components/SubmitSignalModal';
import { PulseIndicator } from './components/PulseIndicator';
import { FilterBar } from './components/FilterBar';
import { ViewToggle } from './components/ViewToggle';
import { AuthModal } from './components/AuthModal';
import { SubscriptionGate } from './components/SubscriptionGate';
import { UserMenu } from './components/UserMenu';
import { SubscriptionSuccess, SubscriptionCancel } from './components/SubscriptionPages';
import { AccountSettings } from './components/AccountSettings';
import { TodayBrief } from './components/TodayBrief';
import { ResetTab } from './components/ResetTab';
import { LandingPage } from './components/LandingPage';
import { PatreonLoginModal } from './components/PatreonLoginModal';
import { PatreonAdminPanel } from './components/PatreonAdminPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth helper - supports both regular auth and Patreon tokens
const getAuthHeader = () => {
  const authType = localStorage.getItem('auth_type');
  if (authType === 'patreon') {
    const token = localStorage.getItem('patreon_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Register service worker for push notifications
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('ServiceWorker registered:', registration.scope);
    } catch (error) {
      console.error('ServiceWorker registration failed:', error);
    }
  }
};

const Home = () => {
  const navigate = useNavigate();
  const [signals, setSignals] = useState([]);
  const [pulseCount, setPulseCount] = useState(0);
  const [pulseInsight, setPulseInsight] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [view, setView] = useState(() => {
    // For logged-in users: use stored preference or default to 'today'
    // For non-logged-in visitors: always show 'all' signals view (teaser/preview)
    const stored = localStorage.getItem('inspiteof_view_preference');
    return stored && stored !== 'today' ? stored : 'today';
  });
  
  // Patreon-related state
  const [isPatreonLoginOpen, setIsPatreonLoginOpen] = useState(false);
  const [isPatreonAdminOpen, setIsPatreonAdminOpen] = useState(false);
  const [patreonSession, setPatreonSession] = useState(null);
  
  // Persist view preference when user explicitly changes tabs
  const handleViewChange = (newView) => {
    setView(newView);
    localStorage.setItem('inspiteof_view_preference', newView);
  };
  
  const [filters, setFilters] = useState({
    category: '',
    country: '',
    city: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    countries: [],
    cities: []
  });
  
  // Auth state
  const [user, setUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  // Stripe config state
  const [stripeConfigured, setStripeConfigured] = useState(false);

  // Determine if user has premium access (subscriber OR Patreon member)
  const hasPremiumAccess = (user?.is_subscribed && user?.subscription_status === 'active') || patreonSession;
  const isSubscribed = user?.is_subscribed && user?.subscription_status === 'active';
  const isPatreonMember = !!patreonSession && !user;

  // Register service worker on mount
  useEffect(() => {
    registerServiceWorker();
  }, []);

  // Check Stripe configuration
  useEffect(() => {
    const checkStripeConfig = async () => {
      try {
        const response = await axios.get(`${API}/subscription/config`);
        setStripeConfigured(response.data.is_configured);
      } catch (error) {
        console.error('Error checking Stripe config:', error);
        setStripeConfigured(false);
      }
    };
    checkStripeConfig();
  }, []);

  // Check existing auth on mount (both regular and Patreon)
  useEffect(() => {
    const checkAuth = async () => {
      const authType = localStorage.getItem('auth_type');
      
      // Check Patreon auth first
      if (authType === 'patreon') {
        const patreonToken = localStorage.getItem('patreon_token');
        if (patreonToken) {
          try {
            const response = await axios.get(`${API}/patreon/me`, {
              headers: { Authorization: `Bearer ${patreonToken}` }
            });
            setPatreonSession(response.data);
            setIsCheckingAuth(false);
            return;
          } catch (err) {
            // Token expired or invalid
            localStorage.removeItem('patreon_token');
            localStorage.removeItem('auth_type');
          }
        }
      }
      
      // Check regular user auth
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (err) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_type');
        }
      }
      setIsCheckingAuth(false);
    };
    checkAuth();
  }, []);

  const fetchSignals = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (hasPremiumAccess) {
        if (filters.category) params.append('category', filters.category);
        if (filters.country) params.append('country', filters.country);
        if (filters.city) params.append('city', filters.city);
        // Only allow my_signals for regular subscribers, not Patreon
        if (view === 'my' && isSubscribed) params.append('my_signals', 'true');
      }

      const queryString = params.toString();
      const url = `${API}/signals${queryString ? `?${queryString}` : ''}`;
      
      const response = await axios.get(url, { headers: getAuthHeader() });
      setSignals(response.data);
    } catch (error) {
      console.error('Error fetching signals:', error);
    }
  }, [filters, view, hasPremiumAccess, isSubscribed]);

  const fetchPulse = async () => {
    try {
      const response = await axios.get(`${API}/signals/pulse`);
      setPulseCount(response.data.count);
      
      // Fetch pulse insight
      const insightResponse = await axios.get(`${API}/intelligence/pulse-insight`);
      setPulseInsight(insightResponse.data.insight);
    } catch (error) {
      console.error('Error fetching pulse:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/signals/filters`, { headers: getAuthHeader() });
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const loadData = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([fetchSignals(), fetchPulse(), fetchFilterOptions()]);
    setIsLoading(false);
  }, [fetchSignals]);

  useEffect(() => {
    if (!isCheckingAuth) {
      loadData();
    }
  }, [loadData, isCheckingAuth]);

  // Refresh data every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSignals();
      fetchPulse();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchSignals]);

  const handleLogin = async (credentials) => {
    const response = await axios.post(`${API}/auth/login`, credentials);
    localStorage.setItem('auth_token', response.data.access_token);
    localStorage.setItem('auth_type', 'user');
    setUser(response.data.user);
    toast.success('Welcome back!');
    await loadData();
  };

  const handleRegister = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    localStorage.setItem('auth_token', response.data.access_token);
    localStorage.setItem('auth_type', 'user');
    setUser(response.data.user);
    toast.success('Account created! Now subscribe to get full access.');
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('patreon_token');
    localStorage.removeItem('auth_type');
    setUser(null);
    setPatreonSession(null);
    setFilters({ category: '', country: '', city: '' });
    setView('all');
    toast.info('Signed out');
    loadData();
  };

  const handlePatreonLoginSuccess = (data) => {
    setPatreonSession(data);
    loadData();
  };

  const handlePatreonLogout = async () => {
    try {
      const token = localStorage.getItem('patreon_token');
      if (token) {
        await axios.post(`${API}/patreon/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (err) {
      // Ignore errors on logout
    }
    localStorage.removeItem('patreon_token');
    localStorage.removeItem('auth_type');
    setPatreonSession(null);
    toast.info('Patreon session ended');
    loadData();
  };

  const handleSubscribe = async () => {
    if (!user) {
      setAuthMode('register');
      setIsAuthModalOpen(true);
      return;
    }

    if (!stripeConfigured) {
      toast.error('Stripe is not yet configured. Please add your Stripe keys.');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/subscription/create-checkout-session`,
        { 
          success_url: `${window.location.origin}/subscription/success`,
          cancel_url: `${window.location.origin}/subscription/cancel`
        },
        { headers: getAuthHeader() }
      );
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    }
  };

  const handleSubmitSignal = async (formData) => {
    try {
      await axios.post(`${API}/signals`, formData, { headers: getAuthHeader() });
      toast.success('Signal submitted successfully');
      await fetchSignals();
      await fetchPulse();
      await fetchFilterOptions();
    } catch (error) {
      console.error('Error submitting signal:', error);
      if (error.response?.status === 403) {
        toast.error('Active subscription required');
      } else {
        toast.error('Failed to submit signal');
      }
      throw error;
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      category: '',
      country: '',
      city: ''
    });
  };

  const handleRefresh = () => {
    loadData();
    toast.info('Refreshing signals...');
  };

  const openAuthModal = (mode) => {
    setAuthMode(mode);
    setIsAuthModalOpen(true);
  };

  // Show loading state
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Radio className="w-8 h-8 text-red-500 animate-pulse" />
      </div>
    );
  }

  // === PUBLIC LANDING PAGE (Non-logged-in visitors) ===
  if (!user && !patreonSession) {
    return (
      <>
        <LandingPage 
          onJoinClick={() => openAuthModal('register')}
          onSignInClick={() => openAuthModal('login')}
          onPatreonClick={() => setIsPatreonLoginOpen(true)}
        />
        {/* Auth Modal */}
        <AuthModal
          open={isAuthModalOpen}
          onOpenChange={setIsAuthModalOpen}
          onLogin={handleLogin}
          onRegister={handleRegister}
          initialMode={authMode}
        />
        {/* Patreon Login Modal */}
        <PatreonLoginModal
          open={isPatreonLoginOpen}
          onOpenChange={setIsPatreonLoginOpen}
          onLoginSuccess={handlePatreonLoginSuccess}
        />
        <Toaster 
          position="top-center" 
          toastOptions={{
            style: {
              background: '#18181b',
              border: '1px solid #27272a',
              color: '#fafafa'
            }
          }}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-zinc-950/80 border-b border-zinc-800">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center">
                <Radio className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg text-zinc-100 tracking-tight" data-testid="app-title">
                  InSpiteOf
                </h1>
                <p className="text-xs text-zinc-500">Private Signal Network</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                className="text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              
              {/* Admin button - only for regular subscribers (not Patreon) */}
              {isSubscribed && user?.email === 'yourrecoverymentor@gmail.com' && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsPatreonAdminOpen(true)}
                  className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                  data-testid="patreon-admin-btn"
                  title="Patreon Admin"
                >
                  <Shield className="w-4 h-4" />
                </Button>
              )}
              
              {/* Submit Signal - only for regular subscribers (not Patreon) */}
              {isSubscribed && (
                <Button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2"
                  data-testid="submit-signal-btn-header"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Submit Signal</span>
                </Button>
              )}
              
              {/* User Menu for regular users */}
              {user && (
                <UserMenu 
                  user={user} 
                  onLogout={handleLogout}
                  onManageSubscription={handleSubscribe}
                />
              )}
              
              {/* Patreon member badge and logout */}
              {isPatreonMember && (
                <div className="flex items-center gap-2">
                  <span className="bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5">
                    <Shield className="w-3 h-3" />
                    Patreon
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handlePatreonLogout}
                    className="text-zinc-400 hover:text-zinc-200 text-xs"
                  >
                    Sign Out
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Patreon Admin Panel */}
      {isPatreonAdminOpen && (
        <PatreonAdminPanel onClose={() => setIsPatreonAdminOpen(false)} />
      )}

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Pulse Section */}
        <PulseIndicator count={pulseCount} insight={pulseInsight} />

        {/* Subscription Gate for non-subscribed logged-in users (not for Patreon members) */}
        {!hasPremiumAccess && user && (
          <SubscriptionGate 
            onSubscribe={handleSubscribe} 
            pulseCount={pulseCount}
            isConfigured={stripeConfigured}
          />
        )}

        {/* View Toggle - visible to users with premium access */}
        <div className={!hasPremiumAccess ? 'opacity-50 pointer-events-none blur-sm' : ''}>
          <ViewToggle view={view} onViewChange={handleViewChange} isPatreonMember={isPatreonMember} />

          {/* Filters - only show for all/my views, not today or reset */}
          {(view === 'all' || view === 'my') && (
            <div className="mt-4">
              <FilterBar 
                filters={filters}
                onFilterChange={handleFilterChange}
                filterOptions={filterOptions}
                onClearFilters={handleClearFilters}
              />
            </div>
          )}
        </div>

        {/* Today Intelligence Briefing - Users with premium access */}
        {view === 'today' && hasPremiumAccess && (
          <TodayBrief isSubscribed={true} />
        )}
        
        {/* Today preview for logged-in non-subscribed users (not Patreon) */}
        {view === 'today' && !hasPremiumAccess && user && (
          <div className="space-y-4">
            <TodayBrief isSubscribed={false} />
            <div className="text-center py-6 bg-zinc-900/80 rounded-lg border border-zinc-800">
              <p className="text-zinc-400 mb-3">Subscribe to access full intelligence briefings</p>
              <button
                onClick={handleSubscribe}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2 rounded-lg"
                data-testid="subscribe-today-btn"
              >
                Subscribe for $7/month
              </button>
            </div>
          </div>
        )}

        {/* Reset Tab - Mental wellness / grounding content */}
        {view === 'reset' && (
          <ResetTab />
        )}

        {/* Signals Feed - show for all/my views */}
        {(view === 'all' || (view === 'my' && !isPatreonMember)) && (
          <div className="space-y-4" data-testid="signals-feed">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 animate-pulse">
                  <div className="h-4 bg-zinc-800 rounded w-1/4 mb-3" />
                  <div className="h-4 bg-zinc-800 rounded w-full mb-2" />
                  <div className="h-4 bg-zinc-800 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : signals.length === 0 ? (
            <div className="text-center py-12 bg-zinc-900/50 rounded-lg border border-zinc-800" data-testid="no-signals">
              <Radio className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
              <p className="text-zinc-400 mb-2">No signals found</p>
              <p className="text-sm text-zinc-500">
                {view === 'my' 
                  ? "You haven't submitted any signals yet" 
                  : "Be the first to submit a signal"}
              </p>
            </div>
          ) : (
            <>
              {signals.map((signal, index) => (
                <div 
                  key={signal.id} 
                  className={!hasPremiumAccess && index >= 1 ? 'blur-sm' : ''}
                >
                  <SignalCard signal={signal} />
                </div>
              ))}
              
              {/* Show lock message for non-premium users */}
              {!hasPremiumAccess && signals.length > 0 && (
                <div className="text-center py-8">
                  <p className="text-zinc-500 mb-4">Subscribe to see all signals</p>
                  <Button
                    onClick={handleSubscribe}
                    className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2 rounded-lg"
                    data-testid="subscribe-inline-btn"
                  >
                    Subscribe for $7/month
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
        )}

        {/* Signal count - only show for all/my views */}
        {view !== 'today' && !isLoading && signals.length > 0 && hasPremiumAccess && (
          <p className="text-center text-sm text-zinc-500" data-testid="signal-count">
            Showing {signals.length} signal{signals.length !== 1 ? 's' : ''}
          </p>
        )}
      </main>

      {/* Submit Signal Modal */}
      <SubmitSignalModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSubmit={handleSubmitSignal}
      />

      {/* Auth Modal */}
      <AuthModal
        open={isAuthModalOpen}
        onOpenChange={setIsAuthModalOpen}
        onLogin={handleLogin}
        onRegister={handleRegister}
        initialMode={authMode}
      />

      {/* Toast notifications */}
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: '#18181b',
            border: '1px solid #27272a',
            color: '#fafafa'
          }
        }}
      />
    </div>
  );
};

// Account page wrapper
const AccountPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        navigate('/');
        return;
      }
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        if (!response.data.is_subscribed) {
          navigate('/');
        }
      } catch (err) {
        localStorage.removeItem('auth_token');
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const refreshUser = async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (err) {
        console.error('Error refreshing user:', err);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Radio className="w-8 h-8 text-red-500 animate-pulse" />
      </div>
    );
  }

  return <AccountSettings user={user} onUserUpdate={refreshUser} />;
};

function App() {
  const refreshUser = async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
      } catch (err) {
        console.error('Error refreshing user:', err);
      }
    }
    return null;
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/subscription/success" element={<SubscriptionSuccess onRefreshUser={refreshUser} />} />
          <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
        </Routes>
      </BrowserRouter>
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: '#18181b',
            border: '1px solid #27272a',
            color: '#fafafa'
          }
        }}
      />
    </div>
  );
}

export default App;
